import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { HiX } from "react-icons/hi";
import { useBackToClose } from "../../utils/backToClose";

// Small dialog for chat flows, portalled to <body> so it sits above the header panel and docked windows.
// On phones it's a bottom sheet that slides up.
function ChatModal({ title, onClose, children, footer, width = "max-w-md" }) {
  useBackToClose(true, onClose);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        initial={{ y: 48, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.22 }}
        className={`chat-ui relative w-full ${width} max-h-[92dvh] sm:max-h-[85vh] flex flex-col bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 rounded-t-2xl sm:rounded-xl shadow-2xl pb-[env(safe-area-inset-bottom)] sm:pb-0`}
      >
        <div className="sm:hidden mx-auto mt-2 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <HiX className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">{footer}</div>}
      </motion.div>
    </div>,
    document.body
  );
}

export const modalButton = {
  primary:
    "px-4 py-2 rounded-lg bg-[#2470B7] hover:bg-[#1d5a93] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed",
  secondary:
    "px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-sm font-medium",
  danger: "px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium",
};

export default ChatModal;
